/* Translate between the gateway's sparse `overrides` payload and the editor's
 * full value map (every group/field populated), and the bulk presets the UI
 * exposes ("enable everything", "lossless only", per-family, per-transformer).
 *
 * Configs are stored explicitly: every transformer's on/off state is pinned in
 * `overrides` (params only when they differ from the gateway default), so a
 * profile reads as a complete, self-contained picture rather than a delta whose
 * meaning depends on the unknown env base. */

import type { ConfigOverrides } from "./types";
import {
  GATEWAY_GROUP,
  TRANSFORMERS,
  type ConfigField,
  type EffectiveSchema,
  type FamilyKey,
} from "./schema";

export type ConfigValue = boolean | number | string | string[];
export type GroupValues = Record<string, ConfigValue>;
export type ConfigValues = Record<string, GroupValues>;

interface GroupLike {
  key: string;
  enabledDefault: boolean;
  fields: ConfigField[];
}

/** Every settings group the editor manages: the master plus the transformers.
 *  This is the static fallback; pass an `EffectiveSchema`-derived list (see
 *  `allGroups`) to reconstruct against the gateway's resolved defaults. */
const ALL_GROUPS: GroupLike[] = [GATEWAY_GROUP, ...TRANSFORMERS];

/** The groups the value functions operate on, derived from an effective schema
 *  (gateway-resolved defaults overlaid on the static presentation metadata). */
export function allGroups(schema: EffectiveSchema): GroupLike[] {
  return [schema.gatewayGroup, ...schema.transformers];
}

function sameValue(a: ConfigValue, b: ConfigValue): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }

  return a === b;
}

/** Full default value map for a single group (`enabled` + every field). */
export function groupDefaults(group: GroupLike): GroupValues {
  const out: GroupValues = { enabled: group.enabledDefault };
  for (const f of group.fields) out[f.key] = f.default;
  return out;
}

/** Full default value map for all groups. */
export function defaultValues(groups: GroupLike[] = ALL_GROUPS): ConfigValues {
  const out: ConfigValues = {};
  for (const g of groups) out[g.key] = groupDefaults(g);
  return out;
}

/** Defaults overlaid with a stored config's overrides → the editor's start. */
export function valuesFromOverrides(
  overrides: ConfigOverrides,
  groups: GroupLike[] = ALL_GROUPS,
): ConfigValues {
  const out = defaultValues(groups);
  for (const g of groups) {
    const o = overrides[g.key];
    if (!o) continue;

    for (const f of [{ key: "enabled" } as const, ...g.fields]) {
      if (f.key in o) out[g.key][f.key] = o[f.key] as ConfigValue;
    }
  }
  return out;
}

/** Build the `overrides` payload: every group pins `enabled`; params are
 *  included only when they differ from the gateway default. */
export function overridesFromValues(
  values: ConfigValues,
  groups: GroupLike[] = ALL_GROUPS,
): ConfigOverrides {
  const out: ConfigOverrides = {};
  for (const g of groups) {
    const v = values[g.key] ?? groupDefaults(g);
    const group: Record<string, unknown> = { enabled: v.enabled };
    for (const f of g.fields) {
      if (!sameValue(v[f.key], f.default)) group[f.key] = v[f.key];
    }
    out[g.key] = group;
  }
  return out;
}

// ---- presets ---------------------------------------------------------------
function setEnabled(
  values: ConfigValues,
  keys: string[],
  on: boolean,
): ConfigValues {
  const next = { ...values };
  for (const k of keys) next[k] = { ...next[k], enabled: on };
  return next;
}

const allTransformerKeys = () => TRANSFORMERS.map((t) => t.key);

/** Turn on every transformer (and the master). */
export function applyEnableAll(values: ConfigValues): ConfigValues {
  const next = setEnabled(values, allTransformerKeys(), true);
  next.gateway = { ...next.gateway, enabled: true };
  return next;
}

/** Turn on only the lossless transformers; turn off lossy/routing ones. */
export function applyLosslessOnly(values: ConfigValues): ConfigValues {
  let next = { ...values };
  next.gateway = { ...next.gateway, enabled: true };
  for (const t of TRANSFORMERS) {
    next = setEnabled(next, [t.key], t.safety === "lossless");
  }
  return next;
}

/** Turn off every transformer (the master is left untouched). */
export function applyDisableAllTransformers(
  values: ConfigValues,
): ConfigValues {
  return setEnabled(values, allTransformerKeys(), false);
}

/** Restore the gateway defaults for every group. */
export function applyResetDefaults(
  groups: GroupLike[] = ALL_GROUPS,
): ConfigValues {
  return defaultValues(groups);
}

/** Transformers that rewrite the same `tool_result` blocks and must not stack;
 *  the gateway makes CCR win when both are on, so the UI keeps max one enabled. */
const EXCLUSIVE: Record<string, string> = {
  ccr: "tool_result_truncation",
  tool_result_truncation: "ccr",
};

/** After enabling `group`, force its mutually-exclusive sibling off. No-op for
 *  groups without an exclusivity rule or when `group` was just turned off. */
export function applyExclusivity(
  values: ConfigValues,
  group: string,
): ConfigValues {
  const other = EXCLUSIVE[group];
  if (!other || values[group]?.enabled !== true) return values;

  return setEnabled(values, [other], false);
}

export function setFamilyEnabled(
  values: ConfigValues,
  family: FamilyKey,
  on: boolean,
): ConfigValues {
  return setEnabled(
    values,
    TRANSFORMERS.filter((t) => t.family === family).map((t) => t.key),
    on,
  );
}

// ---- summaries -------------------------------------------------------------
export interface EnabledCount {
  on: number;
  total: number;
}

export function transformerEnabledCount(values: ConfigValues): EnabledCount {
  const on = TRANSFORMERS.filter((t) => values[t.key]?.enabled === true).length;
  return { on, total: TRANSFORMERS.length };
}

export function familyEnabledCount(
  values: ConfigValues,
  family: FamilyKey,
): EnabledCount {
  const members = TRANSFORMERS.filter((t) => t.family === family);
  const on = members.filter((t) => values[t.key]?.enabled === true).length;
  return { on, total: members.length };
}

/** Count enabled transformers directly from a stored overrides payload (for the
 *  list view), honoring schema defaults for groups/fields not pinned. */
export function enabledFromOverrides(
  overrides: ConfigOverrides,
  groups: GroupLike[] = ALL_GROUPS,
): EnabledCount {
  const values = valuesFromOverrides(overrides, groups);
  return transformerEnabledCount(values);
}

// ---- validation ------------------------------------------------------------
export interface ConfigIssue {
  group: string;
  message: string;
}

/** Cross-field checks the server also enforces, surfaced before save. */
export function validateValues(values: ConfigValues): ConfigIssue[] {
  const issues: ConfigIssue[] = [];
  const r = values.routing;
  if (
    r &&
    typeof r.simple_main_loop_min_messages === "number" &&
    typeof r.simple_main_loop_max_messages === "number" &&
    r.simple_main_loop_min_messages > r.simple_main_loop_max_messages
  ) {
    issues.push({
      group: "routing",
      message: "Min messages must be ≤ max messages.",
    });
  }

  return issues;
}
